'use client';
import cn from 'classnames';
import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { FormikProps } from 'formik';

import {
  useApplicationSteps,
  StepId,
  APPLICATION_STEPS
} from '@/components/hook/applicationSteps';
import CollapsibleSection from '@/components/common/CollapsibleSection/CollapsibleSection';

import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';
import { RawEPCCCartResponse } from '@/types/Cart';
import { SignupState } from '@/types/Signup';
import Text from '@/components/common/Text/Text';
import analytics from '@/util/analytics';
import { APPLICATION_FLOW } from '@/const/application_flow';
import { useSignupAnalytics } from '@/components/hook/analytics';

import type { ReferralCopyProps } from '@/components/common/SignUpApp/SignUpApp';
import {
  ApplicationFormValues,
  fieldsForStep,
  stepIsComplete
} from './applicationValidation';
import { useMarketingAbandon } from './useMarketingAbandon';
import ApplicationErrorSummary from './ApplicationErrorSummary';
import { buildErrorItems, ApplicationErrorItem } from './applicationErrors';
import { summaryForStep } from './stepSummaries';
import ApplicationInfoFields from './ApplicationInfoFields';
import ApplicationShippingFields from './ApplicationShippingFields';
import ApplicationPaymentSection, {
  ConfirmPayment,
  PaymentChoice,
  PaymentStatus,
  paymentChoiceLabel
} from './ApplicationPaymentSection';
import ApplicationWalletButtons from './ApplicationWalletButtons';
import ApplicationTermsFields from './ApplicationTermsFields';
import ApplicationReviewCards from './ApplicationReviewCards';
import SummaryDock from './SummaryDock';

const STEP_TITLES: Record<StepId, string> = {
  info: 'My Info',
  shipping: 'Shipping',
  billing: 'Billing Method'
};

/**
 * Runs `onSettled` once `el`'s position stops moving between animation
 * frames, rather than after a fixed delay. A section opening also collapses
 * the previous one, and a fixed delay tuned to one CSS transition's duration
 * undershoots whenever the other is still running — `el` gets measured
 * mid-shift, and the resulting scroll lands wherever that half-collapsed
 * layout happens to put it (observed: off the page entirely, into the
 * footer). Capped at 1s so a stuck layout still resolves to something.
 */
function onLayoutSettled(el: HTMLElement, onSettled: () => void) {
  let lastTop: number | null = null;
  let stableFrames = 0;
  let totalFrames = 0;

  const check = () => {
    totalFrames += 1;
    const top = el.getBoundingClientRect().top;
    stableFrames =
      lastTop !== null && Math.abs(top - lastTop) < 0.5 ? stableFrames + 1 : 0;
    lastTop = top;

    if (stableFrames >= 3 || totalFrames >= 60) {
      onSettled();
    } else {
      requestAnimationFrame(check);
    }
  };

  requestAnimationFrame(check);
}

type ApplicationBodyProps = {
  formik: FormikProps<ApplicationFormValues>;
  steps: ReturnType<typeof useApplicationSteps>;
  onConfirmReady: (confirm: ConfirmPayment | null) => void;
  paymentStatus: PaymentStatus;
  onPaymentStatusChange: (status: PaymentStatus) => void;
  paymentChoice: PaymentChoice;
  onPaymentChoiceChange: (choice: PaymentChoice) => void;
  /** The wallet the applicant actually paid with, once confirmed. */
  walletType: string | null;
  onWalletTypeChange: (type: string | null) => void;
  /** Provisions the application after a wallet confirms its payment method. */
  onWalletConfirmed: (setupIntentId: string) => Promise<void>;
  submissionError: string | null;
  /**
   * Set after the card's billing ZIP is refused. Shown above Billing's Continue,
   * since `submissionError` only renders in the review block and Billing is open.
   */
  addressNotice: string | null;
  /**
   * After a wallet's address was refused: show the billing address with the
   * wallet selected, and send it in place of the wallet's.
   */
  correctWalletBilling: boolean;
  isSubmitting: boolean;
  user: SignupState;
  subs: SubscriptionProduct[];
  insuranceData: InsuranceProduct[];
  previewCart?: RawEPCCCartResponse;
  /** False while a repriced cart is in flight — drives the pending price state. */
  previewCartUpdated: boolean;
  referralCopy?: ReferralCopyProps;
  onCommitValues: (values: Partial<SignupState>) => void;
  suppressAbandon: boolean;
};

function ApplicationBody(props: ApplicationBodyProps) {
  const {
    formik,
    steps,
    onConfirmReady,
    paymentStatus,
    onPaymentStatusChange,
    paymentChoice,
    onPaymentChoiceChange,
    walletType,
    onWalletTypeChange,
    onWalletConfirmed,
    submissionError,
    addressNotice,
    correctWalletBilling,
    isSubmitting,
    user,
    subs,
    insuranceData,
    previewCart,
    previewCartUpdated,
    referralCopy,
    onCommitValues,
    suppressAbandon
  } = props;

  const { values, errors } = formik;

  const { metadata: userSignupAnalytics } = useSignupAnalytics(user);

  /**
   * The legacy multi-page flow fires a funnel event per page submit
   * (`Start/Customer Information/Address/Payment Finished`) — the events any
   * existing funnel/experiment dashboard is built on. This flow has no pages
   * to hang them on, so they fire from each section's own successful
   * Continue instead (`fireStepFunnelEvent`, below). The ref is what keeps
   * each one to once per step per visit: unlike the legacy flow, a section
   * here can be reopened and continued again, and a dashboard built on "how
   * many people finished this step" should not count that twice.
   */
  const firedFunnelEventsRef = useRef<Set<StepId>>(new Set());

  /**
   * `initialValues` (in `SinglePageApplication`) seeds Formik from
   * `sessionStorage` on the client but not on the server — a real restored
   * session is therefore already "complete" on the client's very first
   * render, while the server rendered it empty. Completeness driving whether
   * a `<div>` exists at all (the collapsed summary, below) turns that values
   * mismatch into a hydration error, not just a harmless prop warning. Until
   * mounted, report every step as its SSR-safe (incomplete) self so the first
   * client render matches the server; the real, restored completeness lands
   * a tick later, same as any other post-mount state.
   */
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const completeness: Record<StepId, boolean> = !hasMounted
    ? { info: false, shipping: false, billing: false }
    : {
        info: stepIsComplete('info', values, errors),
        shipping: stepIsComplete('shipping', values, errors),
        /**
         * A wallet collects nothing on the page — the sheet does that at submit —
         * so choosing it is all this section needs. The card path still waits for
         * the element to report complete.
         */
        billing: stepIsComplete(
          'billing',
          values,
          errors,
          paymentChoice !== 'card' || paymentStatus.complete
        )
      };

  const allComplete = APPLICATION_STEPS.every((step) => completeness[step]);

  /**
   * The review block (cards + consents + Submit) replaces the per-section
   * Continue as the page's single primary action, once every section is done
   * and closed. Reopening a section to edit hands the action back to that
   * section's Continue.
   */
  const reviewReady = allComplete && !steps.anyOpen;

  /**
   * Submit is always enabled (a permanently-disabled submit is an a11y
   * anti-pattern that never says why). When it is pressed with missing/invalid
   * fields, we validate, expand the blocking section, and show a focusable
   * error summary instead of silently doing nothing.
   */
  const [errorItems, setErrorItems] = useState<ApplicationErrorItem[]>([]);

  /**
   * Lifted out of `SummaryDock` so the "Membership Details" review card can
   * open the same editor its own Edit control does — the two are siblings
   * here, not a parent/child pair.
   */
  const [summaryOpen, setSummaryOpen] = useState(false);

  const addressNoticeRef = useRef<HTMLDivElement>(null);

  // Billing was just opened and may be below the fold. Wait for its expand
  // transition, like each section's Continue does, then bring the notice in.
  useEffect(() => {
    const notice = addressNoticeRef.current;

    if (addressNotice && notice) {
      onLayoutSettled(notice, () =>
        notice.scrollIntoView({ behavior: 'smooth', block: 'center' })
      );
    }
  }, [addressNotice]);

  /**
   * Set when the chosen wallet's button can't render at submit time. Sends the
   * applicant back to Billing — on the card, with that wallet hidden — rather
   * than leaving a review block with no way to pay.
   */
  const [unavailableChoice, setUnavailableChoice] =
    useState<PaymentChoice | null>(null);

  const handleWalletUnavailable = () => {
    analytics.track('Application - Payment Method Unavailable', {
      application_flow: APPLICATION_FLOW.SINGLE_PAGE,
      payment_method: paymentChoice
    });
    setUnavailableChoice(paymentChoice);
    onPaymentChoiceChange('card');
    steps.requestOpen('billing', true);
  };

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationErrors = await formik.validateForm();
    // Mark every field touched so the inline errors render alongside the summary.
    formik.setTouched(
      Object.keys(values).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      false
    );

    const { step, items } = buildErrorItems(
      validationErrors,
      values,
      paymentChoice !== 'card' || paymentStatus.complete
    );

    if (items.length > 0) {
      if (step) {
        steps.requestOpen(step, true);
      }
      setErrorItems(items);
      return;
    }

    setErrorItems([]);
    await formik.submitForm();
  };

  /**
   * The legacy per-page funnel events, re-anchored to each section's own
   * Continue. "My Info" merges what used to be two pages (account, then
   * personalize), so it fires both of that pair's events together — they
   * happen at the same moment here, which is a real difference from the
   * legacy timing but keeps both event names present for anything built on
   * them.
   */
  const fireStepFunnelEvent = (step: StepId) => {
    if (firedFunnelEventsRef.current.has(step)) {
      return;
    }
    firedFunnelEventsRef.current.add(step);

    if (step === 'info') {
      analytics.track('Application - Start Application Finished', {
        application_flow: APPLICATION_FLOW.SINGLE_PAGE
      });
      analytics.track('Application - Customer Information Finished', {
        application_flow: APPLICATION_FLOW.SINGLE_PAGE,
        success: true,
        ...userSignupAnalytics
      });
      return;
    }

    const eventByStep: Partial<Record<StepId, string>> = {
      shipping: 'Application - Address Finished',
      billing: 'Application - Payment Finished'
    };
    const event = eventByStep[step];

    if (event) {
      analytics.track(event, {
        application_flow: APPLICATION_FLOW.SINGLE_PAGE,
        success: true,
        ...userSignupAnalytics
      });
    }
  };

  /**
   * Each section's own Continue. Validates just that section: clean, it
   * collapses and the next one opens; otherwise its fields are touched so the
   * inline errors appear and it stays put.
   */
  const handleStepContinue = async (step: StepId) => {
    // Judged on a freshly computed validation, never on `completeness` — that
    // reads Formik's last-rendered errors, which lag a click that follows a
    // keystroke and would refuse to advance a section that is actually valid.
    const validationErrors = await formik.validateForm();
    const fields = fieldsForStep(step);
    const stepErrors = fields.filter((field) => !!validationErrors[field]);

    // The payment section has no Formik fields; the element (or the applicant's
    // choice of a wallet) reports for it.
    const blocked =
      step === 'billing'
        ? paymentChoice === 'card' && !paymentStatus.complete
        : stepErrors.length > 0;

    if (blocked) {
      formik.setTouched(
        fields.reduce((acc, field) => ({ ...acc, [field]: true }), {}),
        false
      );
      return;
    }

    fireStepFunnelEvent(step);

    const opened = steps.advance(step);

    if (opened) {
      // Scroll to the section's first field, not just its heading — on
      // mobile, where the next section opens below the fold, the heading
      // alone left the actual fields the applicant needs to fill in (e.g.
      // Shipping's address field) out of view below the keyboard.
      //
      // Scroll only — deliberately not `.focus()`. Shipping's first field is
      // a Google Places Autocomplete input, and focusing it programmatically
      // opens Google's own suggestions dropdown uninvited, before the
      // applicant has typed anything. Scrolling it into view still gets the
      // field on screen; typing into it is left to the applicant's own tap.
      const target =
        document.querySelector<HTMLElement>(
          `[data-testid="application-step-${opened}"] input, [data-testid="application-step-${opened}"] select, [data-testid="application-step-${opened}"] textarea`
        ) ??
        document.querySelector<HTMLElement>(
          `[data-testid="application-section-${opened}-toggle"]`
        );

      if (target) {
        // Waits for both this section's expand *and* the previous one's
        // collapse to actually finish moving `target` — scrolling against
        // its mid-transition position landed the page somewhere else once
        // the still-collapsing section above it kept shrinking afterward.
        onLayoutSettled(target, () => {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    }
  };

  /**
   * Clear the summary once everything it could flag is satisfied, so it goes
   * away when the member fixes things rather than lingering until the next
   * submit. The consents count: they are the most common thing left unticked,
   * and clearing on the sections alone would wipe the summary the instant it
   * rendered.
   */
  const nothingLeftToFlag =
    allComplete && values.paymentAgreement && values.terms;

  useEffect(() => {
    if (nothingLeftToFlag && errorItems.length > 0) {
      setErrorItems([]);
    }
  }, [nothingLeftToFlag, errorItems.length]);

  /**
   * Reopening any section to edit hides the review block (and the summary
   * with it) until every section is closed again. Without this, a summary
   * raised by an earlier failed Submit was still sitting in state and simply
   * reappeared the moment the applicant finished editing — even though they
   * never tried to submit again. It should only come back from an actual
   * new attempt.
   */
  useEffect(() => {
    if (steps.anyOpen && errorItems.length > 0) {
      setErrorItems([]);
    }
  }, [steps.anyOpen, errorItems.length]);

  // Re-anchor the legacy step-1 Braze abandon to the first info completion.
  useMarketingAbandon({
    infoComplete: completeness.info,
    email: values.email,
    optinPromotion: values.optinPromotion,
    suppressed: suppressAbandon
  });

  useEffect(() => {
    APPLICATION_STEPS.forEach((step) =>
      steps.reportComplete(step, completeness[step])
    );
    // `steps` is intentionally excluded: including it would re-run this on
    // every status change it produces.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeness.info, completeness.shipping, completeness.billing]);

  /**
   * Keeps the open section pointed at the first thing that actually needs
   * attention. Two ways this drifts on its own:
   *  - Nothing open at all: no per-section Continue is visible (collapsed
   *    sections have zero height) and no page Submit either (`reviewReady`
   *    requires every step complete) — a dead end with no actionable control
   *    anywhere. Happens when a step is missing something but got closed
   *    anyway, e.g. a restored session whose password never persisted.
   *  - A *later*, unreachable section is open instead: a stale `#billing`
   *    hash from an earlier visit can force Billing open on mount even
   *    though Info is the one still missing something.
   *
   * Reopen the *first* incomplete step, by section order, and only that one
   * — the point is to say where to look next, not to dump every unfinished
   * section on screen at once.
   */
  useEffect(() => {
    if (!hasMounted || allComplete) {
      return;
    }

    const firstIncomplete = APPLICATION_STEPS.find(
      (step) => !completeness[step]
    );

    if (!firstIncomplete || steps.isOpen(firstIncomplete)) {
      return;
    }

    const openStep = APPLICATION_STEPS.find((step) => steps.isOpen(step));

    if (!openStep || !isReachable(openStep)) {
      steps.requestOpen(firstIncomplete, true);
    }
    // `steps` is intentionally excluded: including it would re-run this on
    // every status change it produces.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasMounted,
    steps.anyOpen,
    allComplete,
    completeness.info,
    completeness.shipping,
    completeness.billing
  ]);

  /**
   * An empty recap must stay `undefined` rather than an empty element — an
   * element is always truthy, so wrapping it unconditionally would render a
   * blank caption row under an untouched step.
   */
  const stepSummary = (step: StepId): ReactNode => {
    // Same hydration hazard as `completeness` above: `summaryForStep` reads
    // `values` directly, which a restored session already has on the
    // client's first render but the server never sees.
    if (!hasMounted) {
      return undefined;
    }

    /**
     * The recap waits for the section to be settled before naming a method: the
     * card element reports a type the moment it mounts, and a wallet only names
     * itself once its sheet has confirmed.
     */
    const method = completeness.billing
      ? paymentChoiceLabel(paymentChoice, walletType)
      : null;

    const lines = summaryForStep(step, values, method);

    if (lines.length === 0) {
      return undefined;
    }

    return lines.map((line) => (
      <Text
        key={line}
        variant='text-m'
      >
        {line}
      </Text>
    ));
  };

  /**
   * A step is reachable once every step ahead of it is complete. Locks the
   * flow to one section at a time in order — Shipping cannot be opened while
   * My Info is unfinished, and so on.
   */
  const isReachable = (step: StepId) =>
    APPLICATION_STEPS.slice(0, APPLICATION_STEPS.indexOf(step)).every(
      (priorStep) => completeness[priorStep]
    );

  /**
   * A section that is done and closed shows its recap and an Edit control —
   * but only once it's also reachable. Without that second check, filling
   * Shipping/Billing out of order (a restored session, a direct hash link)
   * while an earlier step is still missing something left a section reading
   * as both "locked" (grey, from `--locked` below) and "done" (its own recap
   * and Edit) at the same time — the grey box said one thing, the text said
   * another.
   */
  const isRecapped = (step: StepId) =>
    !steps.isOpen(step) && completeness[step] && isReachable(step);

  const stepContent: Record<StepId, ReactNode> = {
    info: (
      <ApplicationInfoFields
        referralCopy={referralCopy}
        appliedReferralCode={user.referralCode}
        onReferralCommit={(referralCode) => onCommitValues({ referralCode })}
      />
    ),
    shipping: <ApplicationShippingFields />,
    billing: (
      <ApplicationPaymentSection
        user={user}
        choice={paymentChoice}
        onChoiceChange={onPaymentChoiceChange}
        onConfirmReady={onConfirmReady}
        onStatusChange={onPaymentStatusChange}
        isOpen={steps.isOpen('billing')}
        unavailableChoice={unavailableChoice}
        showWalletBilling={correctWalletBilling}
      />
    )
  };

  /**
   * Push the form's values into `SignupContext` whenever a field is left.
   *
   * Formik owns the inputs, but the context is what the summary reads and what
   * `useSignupPersistence` writes to sessionStorage — so without this, a reload
   * restored nothing except the referral code (the one value that had its own
   * commit). Blur is the checkpoint rather than change: committing per keystroke
   * would re-run the preview cart effect on every character.
   */
  const commitToContext = () => onCommitValues(values as Partial<SignupState>);

  return (
    <form
      className='SinglePageApplication-form'
      onSubmit={handleFormSubmit}
      onBlur={commitToContext}
      noValidate
    >
      <div className='SinglePageApplication-steps'>
        {/* The design shows no page title, but the document still needs one. */}
        <Text
          as='h1'
          variant='title-xxs-700-upper'
          className='SinglePageApplication-title'
        >
          Membership application
        </Text>

        {APPLICATION_STEPS.map((step) => {
          const locked = !steps.isOpen(step) && !isReachable(step);

          return (
            <CollapsibleSection
              key={step}
              className={cn(
                'SinglePageApplication-step',
                `SinglePageApplication-step--${step}`,
                {
                  'SinglePageApplication-step--recapped': isRecapped(step),
                  'SinglePageApplication-step--locked': locked
                }
              )}
              title={
                <Text
                  as='h2'
                  variant='title-xxs-700-upper'
                >
                  {STEP_TITLES[step]}
                </Text>
              }
              open={steps.isOpen(step)}
              onRequestChange={(open) => steps.requestOpen(step, open)}
              disabled={locked}
              summary={stepSummary(step)}
              headerAside={
                // The design puts an Edit control on a section you have already
                // filled in, in place of the chevron — two affordances for the
                // same job read as clutter, and "edit" is the accurate label.
                isRecapped(step) ? (
                  <button
                    type='button'
                    className='SinglePageApplication-step-edit cursor'
                    onClick={() => steps.requestOpen(step, true)}
                    data-testid={`application-edit-${step}`}
                  >
                    <Text variant='title-xxs-500-upper'>Edit</Text>
                  </button>
                ) : undefined
              }
              testId={`application-section-${step}`}
            >
              <div data-testid={`application-step-${step}`}>
                {stepContent[step]}

                {step === 'billing' && addressNotice && (
                  <div
                    ref={addressNoticeRef}
                    className='SinglePageApplication-address-notice'
                    role='alert'
                    data-testid='application-address-notice'
                  >
                    <Text className='SinglePageApplication-error'>
                      {addressNotice}
                    </Text>
                  </div>
                )}

                {/*
                Per-section advance, per the V2 design. Explicit — nothing
                collapses on its own, so a section can never close under
                someone who is still editing it.
              */}
                <button
                  type='button'
                  className='SinglePageApplication-step-continue cursor'
                  onClick={() => handleStepContinue(step)}
                  data-testid={`application-continue-${step}`}
                >
                  Continue
                </button>
              </div>
            </CollapsibleSection>
          );
        })}

        {/*
          Everything below belongs to the design's final state: all three
          sections done and closed. Until then the only primary action on the
          page is the open section's own Continue — the review cards, the
          consents and Submit do not exist yet.

          Waiting for the sections to close matters as much as waiting for
          completeness: revealing this block the instant the last card digit
          landed would shove the page upward while the member is still looking
          at the field they are typing in.
        */}
        {reviewReady && (
          <>
            <ApplicationReviewCards
              user={user}
              values={values}
              previewCart={previewCart}
              previewCartUpdated={previewCartUpdated}
              subs={subs}
              insuranceData={insuranceData}
              referralCopy={referralCopy}
              onEditMembership={() => setSummaryOpen(true)}
            />

            <ApplicationTermsFields />

            <ApplicationErrorSummary items={errorItems} />

            {submissionError && (
              <Text className='SinglePageApplication-error'>
                {submissionError}
              </Text>
            )}

            {paymentChoice !== 'card' ? (
              /**
               * The wallet button is the submit: Apple requires its own button
               * to open the payment sheet, and that one gesture both authorizes
               * the method and sends the application. It sits here, under the
               * consents, because consent has to come first.
               */
              <ApplicationWalletButtons
                key={paymentChoice}
                user={user}
                choice={paymentChoice}
                onUnavailable={handleWalletUnavailable}
                onBeforeOpen={() => {
                  const { items } = buildErrorItems(errors, values, true);

                  if (items.length > 0) {
                    formik.setTouched(
                      Object.keys(values).reduce(
                        (acc, key) => ({ ...acc, [key]: true }),
                        {}
                      ),
                      false
                    );
                    setErrorItems(items);
                    return false;
                  }

                  setErrorItems([]);
                  return true;
                }}
                onWalletTypeChange={onWalletTypeChange}
                useFormBilling={correctWalletBilling}
                onConfirmed={onWalletConfirmed}
              />
            ) : (
              /*
                Enabled even with the consents unticked — pressing it produces the
                focusable error summary above rather than a dead control. It is
                only disabled mid-submit, where a second press would
                double-provision.
              */
              <button
                className='SinglePageApplication-continue cursor'
                type='submit'
                disabled={isSubmitting}
                data-testid='application-continue'
              >
                {isSubmitting ? 'Submitting…' : 'Submit application'}
              </button>
            )}
          </>
        )}
      </div>

      <SummaryDock
        user={user}
        subs={subs}
        insuranceData={insuranceData}
        previewCart={previewCart}
        previewCartUpdated={previewCartUpdated}
        referralCopy={referralCopy}
        onCommitValues={onCommitValues}
        open={summaryOpen}
        onOpenChange={setSummaryOpen}
        showReadout={!reviewReady}
      />
    </form>
  );
}

export default ApplicationBody;
